import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { Download } from 'lucide-react';
import { tokens } from './tokens';

/**
 * Shared card chrome for every Copilot chart: radius/padding/shadow. The entrance animation lives on
 * the transcript turn (MessageBubble owns the single `cardEntrance` for the whole answer) — wrapping
 * here too would double-animate the card. In the transcript the assistant card sits under the user's
 * question turn, so an echoed title would just repeat it — an empty/blank title omits the title row.
 */
export function ChartCard({
  title,
  children,
  downloadable = false,
}: {
  title: string;
  children: React.ReactNode;
  downloadable?: boolean;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const downloadGraph = () => {
    const canvas = cardRef.current?.querySelector<HTMLCanvasElement>('canvas');
    if (!canvas) return;
    try {
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = 'studysync-copilot-graph.png';
      link.click();
    } catch {
      // A failed image export should not interrupt the chat or chart rendering.
    }
  };

  return (
    <Box
      ref={cardRef}
      sx={{
        bgcolor: tokens.surface.card,
        border: `1px solid ${tokens.surface.border}`,
        borderRadius: '16px',
        p: 3,
        boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 1px 2px rgba(15,23,42,0.04)',
      }}
    >
      {title && title.trim() && (
        <Typography
          sx={{ fontFamily: tokens.fontFamily, fontSize: 16, fontWeight: 600, color: tokens.text.primary, mb: 1.5 }}
        >
          {title}
        </Typography>
      )}
      {children}
      {downloadable && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <button
            type="button"
            onClick={downloadGraph}
            aria-label="Download graph as PNG"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 8px',
              border: 'none',
              borderRadius: 6,
              background: 'transparent',
              color: tokens.text.secondary,
              cursor: 'pointer',
              fontFamily: tokens.fontFamily,
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            <Download size={14} aria-hidden="true" />
            Download graph
          </button>
        </Box>
      )}
    </Box>
  );
}
